import * as React from 'react'
import { Helmet } from 'react-helmet'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Link } from 'react-router-dom'
import { CheckIcon, XIcon, Trash2Icon, MailIcon } from 'lucide-react'
import { Button } from '../../components/ui/button'
import { Input } from '../../components/ui/input'
import { Field, FieldDescription, FieldLabel } from '../../components/ui/field'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table'
import { ConnectionsController } from '../../controllers/accountant'
import { workspacePath } from '../../accountant/ERouteAccountant'
import { AccountantConnection } from '../../types'

interface IProps {
  connections: AccountantConnection[]
  onChange: () => Promise<void>
}

export default function Clients({ connections, onChange }: IProps) {
  const { t } = useTranslation()
  const [email, setEmail] = React.useState('')
  const [isSubmitting, setIsSubmitting] = React.useState(false)

  const accepted = connections.filter(c => c.status === 'accepted')
  const invitations = connections.filter(c => c.status === 'pending' && c.initiated_by === 'workspace')

  const onRequestSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsSubmitting(true)
    try {
      await ConnectionsController.requestAccess(email)
      toast.success(t('Accountant::If this email belongs to a Bizzey account owner, they have received your request.'))
      setEmail('')
    } catch (error) {
      toast.error(t('Accountant::Something went wrong, please try again later.'))
    } finally {
      setIsSubmitting(false)
    }
  }

  const runAction = async (action: () => Promise<unknown>, successMessage: string) => {
    try {
      await action()
      toast.success(successMessage)
      await onChange()
    } catch (error) {
      toast.error(t('Accountant::Something went wrong, please try again later.'))
    }
  }

  return (
    <>
      <Helmet><title>{`Bizzey | ${t('Accountant::Clients')}`}</title></Helmet>

      <div className='flex flex-col gap-6'>
        <Card>
          <CardHeader>
            <CardTitle>{t('Accountant::Request access to a client')}</CardTitle>
            <CardDescription>
              {t('Accountant::Enter the email address your client uses to log in to Bizzey. They will receive a request to approve.')}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={onRequestSubmit} className='flex flex-col gap-4 sm:flex-row sm:items-end'>
              <Field className='flex-1'>
                <FieldLabel htmlFor='client-email'>{t('Accountant::Client email')}</FieldLabel>
                <Input id='client-email' type='email' required value={email} onChange={e => setEmail(e.currentTarget.value)} />
              </Field>
              <Button type='submit' disabled={isSubmitting}>
                <MailIcon />
                {t('Accountant::Send request')}
              </Button>
            </form>
          </CardContent>
        </Card>

        {invitations.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle>{t('Accountant::Invitations')}</CardTitle>
            </CardHeader>
            <CardContent className='flex flex-col gap-2'>
              {invitations.map(invitation => (
                <div key={invitation.id} className='flex items-center justify-between gap-4 rounded-lg border p-3'>
                  <span className='font-medium'>{invitation.workspace.business_name}</span>
                  <div className='flex gap-2'>
                    <Button size='sm' onClick={() => runAction(() => ConnectionsController.accept(invitation.id), t('Accountant::Invitation accepted'))}>
                      <CheckIcon />
                      {t('Accountant::Accept')}
                    </Button>
                    <Button size='sm' variant='outline' onClick={() => runAction(() => ConnectionsController.remove(invitation.id), t('Accountant::Invitation declined'))}>
                      <XIcon />
                      {t('Accountant::Decline')}
                    </Button>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        <Card>
          <CardHeader>
            <CardTitle>{t('Accountant::Connected clients')}</CardTitle>
          </CardHeader>
          <CardContent>
            {accepted.length === 0 ? (
              <FieldDescription>{t('Accountant::You are not connected to any clients yet.')}</FieldDescription>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('Accountant::Client')}</TableHead>
                    <TableHead />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {accepted.map(connection => (
                    <TableRow key={connection.id}>
                      <TableCell>
                        <Link to={workspacePath(connection.workspace.id)} className='font-medium underline-offset-4 hover:underline'>
                          {connection.workspace.business_name}
                        </Link>
                      </TableCell>
                      <TableCell className='text-right'>
                        <Button size='sm' variant='ghost' onClick={() => runAction(() => ConnectionsController.remove(connection.id), t('Accountant::Client removed'))}>
                          <Trash2Icon />
                          {t('Accountant::Remove')}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
    </>
  )
}
